import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeCircles } from 'react-loader-spinner';

import AppModal from './AppModal';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import ExcelJS from 'exceljs';

import './UsersAlertsManagementReport.scss';

import {
  fetchAlertsStatusChangesSummaryReport,
  fetchAlertsStatusChangesSummaryReportRange,
} from '../../actions/CustomersActions';
import UIDateFormater, {
  DateType,
} from '../../containers/UIhelper/UIDateFormater';
import useProjectTimezone from '../../hooks/useProjectTimeZone';
import { validateReportRange } from './usersReportRange';
import { Margin, Padding } from '@mui/icons-material';

const exportButtonStyle = {
  width: 'max-content',
  fontSize: 14,
  fontWeight: 600,
};
const filterStackStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 'unset',
  maxWidth: 'unset',
  width: 'fit-content',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
};
const filterRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: 8,
};
const buttonStackStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  alignItems: 'flex-start',
  minWidth: 'unset',
  maxWidth: 'unset',
  width: 'fit-content',
  justifyContent: 'flex-start',
};
const todayIso = new Date().toISOString().split('T')[0];
const startOfCurrentYearIso = `${new Date().getFullYear()}-01-01`;

const prettifyColumnLabel = (key) =>
  `${key}`
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const rowCellStyle = {
  maxWidth: 150,
  maxHeight: 50,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
const rowCellContentStyle = {
  display: 'block',
  maxWidth: 150,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const dateKeyPattern = /(date|time|created|updated|changed|timestamp)/i;
const atSuffixDateKeyPattern = /_at$/i;
const isManualKeyPattern = /^is_?manual$/i;
const excludedUnixLikeKeyPattern =
  /(id|count|total|priority|type|status|code|index|version|meter|flow|pressure|rate|score|distance|length)/i;
const isoDatePattern =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
const unixLikePattern = /^\d{10}(?:\.\d+)?$|^\d{13}$/;

const normalizeDateValue = (value) => {
  if (typeof value === 'number') {
    if (value > 1e12) return value;
    if (value > 1e9) return value * 1000;
    return null;
  }

  if (typeof value === 'string' && /^\d+(?:\.\d+)?$/.test(value)) {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;
    if (numericValue > 1e12) return numericValue;
    if (numericValue > 1e9) return numericValue * 1000;
    return null;
  }

  if (typeof value === 'string' && isoDatePattern.test(value)) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
};

const isLikelyUnixTimestamp = (value) =>
  (typeof value === 'number' && value > 1e9) ||
  (typeof value === 'string' && unixLikePattern.test(value));

const formatCellValue = (column, value, timeZoneMode, projectTimezone) => {
  if (value == null || value === '') {
    return '--';
  }

  if (isManualKeyPattern.test(column)) {
    return 'Yes';
  }

  const shouldFormatAsDate =
    atSuffixDateKeyPattern.test(column) ||
    dateKeyPattern.test(column) ||
    (!excludedUnixLikeKeyPattern.test(column) && isLikelyUnixTimestamp(value));

  if (shouldFormatAsDate) {
    const normalized = normalizeDateValue(value);
    if (normalized != null) {
      const hasTime =
        typeof value === 'number' ||
        (typeof value === 'string' &&
          (/[T\s]\d{2}:\d{2}/.test(value) || /^\d+(?:\.\d+)?$/.test(value)));
      return UIDateFormater(
        normalized,
        hasTime ? DateType.DATE_AND_TIME : DateType.DATE,
        timeZoneMode,
        projectTimezone
      );
    }
  }

  return `${value}`;
};

const getSortValue = (row, key) => {
  const value = row[key];
  if (value == null) return '';
  if (/^alert_?id$/i.test(key) && typeof value === 'string') {
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) return numericValue;
  }
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return value.toLowerCase();
  return `${value}`;
};

const sortRows = (data, sortConfig) => {
  if (!sortConfig.key) {
    return data;
  }
  const factor = sortConfig.direction === 'asc' ? 1 : -1;
  return [...data].sort((a, b) => {
    const av = getSortValue(a, sortConfig.key);
    const bv = getSortValue(b, sortConfig.key);
    if (av < bv) return -1 * factor;
    if (av > bv) return 1 * factor;
    return 0;
  });
};

const ReportFilter = ({
  context,
  isGenerated,
  onGeneratedReport,
  customFrom,
  setCustomFrom,
  customTo,
  setCustomTo,
  onDownload,
  isDownloadDisabled,
  timezoneLabel,
}) => {
  const range = validateReportRange(customFrom, customTo);
  const showRangeError = customFrom && customTo && !range.valid;
  const onClickGeneratedReport = useCallback(() => {
    if (!range.valid) {
      return;
    }
    onGeneratedReport({ from: customFrom, to: customTo });
  }, [customFrom, customTo, onGeneratedReport, range.valid]);

  const today = new Date().toISOString().split('T')[0];
  const generateDisabled = isGenerated || !range.valid;

  return (
    <Fragment>
      <div
        className='checkbox-iq100b-mobile-type-view-daily'
        style={filterStackStyle}
      >
        <div style={filterRowStyle}>
          <label>{context.t('from')}&nbsp;</label>
          <input
            type='date'
            min={'2025-01-01'}
            max={today}
            onChange={(e) => setCustomFrom(e.target.value)}
            value={customFrom}
          />
        </div>
        <div
          style={{
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 8,
          }}
        >
          <label>{context.t('to')}:&nbsp;</label>
          <input
            type='date'
            min={'2025-01-01'}
            max={today}
            onChange={(e) => setCustomTo(e.target.value)}
            value={customTo}
          />
        </div>
        {showRangeError && (
          <div style={{ color: '#c62828', fontSize: 12, marginTop: 4 }}>
            {context.t(range.error)}
          </div>
        )}
      </div>
      <div
        className='checkbox-iq100b-mobile-button-view'
        style={buttonStackStyle}
      >
        <Button
          disabled={generateDisabled}
          style={exportButtonStyle}
          variant='contained'
          color='primary'
          onClick={onClickGeneratedReport}
        >
          {context.t('generate_report')}
        </Button>
        <Button
          disabled={isDownloadDisabled}
          style={exportButtonStyle}
          variant='contained'
          color='primary'
          onClick={onDownload}
        >
          {context.t('download')}
        </Button>
      </div>
      <div style={{ marginTop: 8, maxWidth: 260, color: '#666', fontSize: 12 }}>
        {context.t('times_are_in_timezone', { timezone: timezoneLabel })}
      </div>
    </Fragment>
  );
};

ReportFilter.propTypes = {
  context: PropTypes.object,
  isGenerated: PropTypes.bool,
  onGeneratedReport: PropTypes.func,
  customFrom: PropTypes.string,
  setCustomFrom: PropTypes.func,
  customTo: PropTypes.string,
  setCustomTo: PropTypes.func,
  onDownload: PropTypes.func,
  isDownloadDisabled: PropTypes.bool,
  timezoneLabel: PropTypes.string,
};

function SummaryTable({
  columns,
  data,
  sortConfig,
  onSort,
  timeZoneMode,
  projectTimezone,
}) {
  return (
    <TableContainer
      className='uamr-table-container'
      style={{ height: 'calc(75vh - 40px)', maxHeight: 'calc(75vh - 40px)' }}
    >
      <Table stickyHeader size='small'>
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            {columns.map((column) => (
              <TableCell
                key={`header_${column}`}
                sortDirection={
                  sortConfig.key === column ? sortConfig.direction : false
                }
                // style={{ maxWidth: 150 }}
              >
                <TableSortLabel
                  active={sortConfig.key === column}
                  direction={
                    sortConfig.key === column ? sortConfig.direction : 'asc'
                  }
                  onClick={() => onSort(column)}
                >
                  {prettifyColumnLabel(column)}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow hover key={`row_${index}`} style={{ height: 50 }}>
              <TableCell style={rowCellStyle}>{index + 1}</TableCell>
              {columns.map((column) => (
                <TableCell key={column} style={rowCellStyle}>
                  <Tooltip
                    title={formatCellValue(
                      column,
                      row[column],
                      timeZoneMode,
                      projectTimezone
                    )}
                    enterDelay={700}
                    arrow
                  >
                    <span style={rowCellContentStyle}>
                      {formatCellValue(
                        column,
                        row[column],
                        timeZoneMode,
                        projectTimezone
                      )}
                    </span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

SummaryTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  sortConfig: PropTypes.object,
  onSort: PropTypes.func,
  timeZoneMode: PropTypes.string,
  projectTimezone: PropTypes.string,
};

function AlertsStatusChangesSummaryModal(
  { selectedProject, isVisible, close },
  context
) {
  const dispatch = useDispatch();
  const timeZoneMode = useSelector((state) => state.local.timeZone);
  const projectTimezone = useProjectTimezone();
  const [isLoading, setIsLoading] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [customFrom, setCustomFrom] = useState(startOfCurrentYearIso);
  const [customTo, setCustomTo] = useState(todayIso);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const projectId = selectedProject ? selectedProject.ID : null;
  const prevProjectIdRef = useRef(projectId);
  useEffect(() => {
    if (prevProjectIdRef.current === projectId) {
      return;
    }
    prevProjectIdRef.current = projectId;
    setData([]);
    setColumns([]);
    setHasGenerated(false);
    setSortConfig({ key: null, direction: 'asc' });
    if (isVisible) {
      close();
    }
  }, [projectId, isVisible, close]);

  const onSort = useCallback((key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const visibleData = useMemo(
    () => sortRows(data, sortConfig),
    [data, sortConfig]
  );

  const timezoneLabel = useMemo(() => {
    if (timeZoneMode === 'project') {
      return projectTimezone || 'project';
    }
    if (timeZoneMode === 'local') {
      return 'local';
    }
    if (timeZoneMode === 'gmt') {
      return 'GMT';
    }
    return timeZoneMode || 'selected';
  }, [timeZoneMode, projectTimezone]);

  const onGeneratedReport = useCallback(
    ({ from, to }) => {
      const request = dispatch(
        fetchAlertsStatusChangesSummaryReportRange(from, to)
      );
      setIsLoading(true);
      setHasGenerated(true);
      request
        .then((response) => {
          if (response?.status) {
            const reportData = Array.isArray(response.data)
              ? response.data
              : Array.isArray(response.data?.data)
              ? response.data.data
              : [];
            setData(reportData);
            setColumns(reportData.length ? Object.keys(reportData[0]) : []);
          } else {
            setData([]);
            setColumns([]);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [dispatch]
  );

  const handleDownload = useCallback(() => {
    if (!columns.length || !visibleData.length) {
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Status Changes Summary');

    worksheet.columns = columns.map((column) => ({
      header: prettifyColumnLabel(column),
      key: column,
      width: 22,
    }));

    visibleData.forEach((row) => {
      const formattedRow = {};
      columns.forEach((column) => {
        formattedRow[column] = formatCellValue(
          column,
          row[column],
          timeZoneMode,
          projectTimezone
        );
      });
      worksheet.addRow(formattedRow);
    });

    worksheet.addRow([]);
    worksheet.addRow([
      context.t('times_are_in_timezone', { timezone: timezoneLabel }),
    ]);

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      const href = window.URL.createObjectURL(blob);
      link.href = href;
      link.download = `${
        selectedProject?.Name || 'alerts-status-changes-summary'
      }_${customFrom}_${customTo}_AlertsStatusChangesSummary.xlsx`;
      link.click();
      window.URL.revokeObjectURL(href);
    });
  }, [
    columns,
    visibleData,
    timeZoneMode,
    projectTimezone,
    selectedProject,
    customFrom,
    customTo,
  ]);

  return (
    <AppModal
      closeIcon
      fullWidth
      maxWidth='xl'
      open={isVisible}
      title={context.t('alerts_status_changes_summary')}
      onClose={close}
      content={
        <div style={{ height: '75vh', overflow: 'hidden' }}>
          {selectedProject && (
            <div style={{ display: 'flex' }}>
              <div>
                <ReportFilter
                  context={context}
                  isGenerated={isLoading}
                  onGeneratedReport={onGeneratedReport}
                  customFrom={customFrom}
                  setCustomFrom={setCustomFrom}
                  customTo={customTo}
                  setCustomTo={setCustomTo}
                  onDownload={handleDownload}
                  isDownloadDisabled={!visibleData.length}
                  timezoneLabel={timezoneLabel}
                />
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  flex: 1,
                  margin: 10,
                  marginLeft: 50,
                  alignItems: 'stretch',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {isLoading ? (
                  <ThreeCircles height='30' width='30' color='#6090B1' />
                ) : columns.length && visibleData.length ? (
                  <SummaryTable
                    columns={columns}
                    data={visibleData}
                    sortConfig={sortConfig}
                    onSort={onSort}
                    timeZoneMode={timeZoneMode}
                    projectTimezone={projectTimezone}
                  />
                ) : hasGenerated ? (
                  <Typography component='p' variant='body2'>
                    {context.t('no_data_available')}
                  </Typography>
                ) : null}
              </div>
            </div>
          )}
        </div>
      }
    />
  );
}

AlertsStatusChangesSummaryModal.contextTypes = {
  t: PropTypes.func.isRequired,
};

AlertsStatusChangesSummaryModal.propTypes = {
  close: PropTypes.func,
  isVisible: PropTypes.bool,
  selectedProject: PropTypes.object,
};

export default React.memo(AlertsStatusChangesSummaryModal);
